// Mobile Quick Login (WebAuthn / passkey) client helper.
// The OS verifies the biometric and unlocks a platform credential; we only ever
// move a signed assertion. The BACKEND verifies it and mints the JWT session.
import api from '@/lib/api';

const LS_HINT = 'sc_quick_user';   // { username, name, role, store } — convenience hint only

const b64urlToBuf = (s) => {
  const pad = '='.repeat((4 - (s.length % 4)) % 4);
  const b = atob((s + pad).replace(/-/g, '+').replace(/_/g, '/'));
  const out = new Uint8Array(b.length);
  for (let i = 0; i < b.length; i++) out[i] = b.charCodeAt(i);
  return out.buffer;
};
const bufToB64url = (buf) => {
  const bytes = new Uint8Array(buf);
  let s = '';
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

// Only mobile + a platform authenticator (Face ID / Touch ID / fingerprint) qualifies.
export const isMobile = () => typeof navigator !== 'undefined'
  && /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent || '');

export async function isQuickLoginAvailable() {
  try {
    if (typeof window === 'undefined' || !window.PublicKeyCredential) return false;
    if (!window.isSecureContext) return false;
    return await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch (_) { return false; }
}

export const getQuickHint = () => {
  try { return JSON.parse(localStorage.getItem(LS_HINT) || 'null'); } catch (_) { return null; }
};
export const setQuickHint = (u) => {
  try { localStorage.setItem(LS_HINT, JSON.stringify({ username: u.username, name: u.name, role: u.role, store_id: u.store_id })); } catch (_) { /* ignore */ }
};
export const clearQuickHint = () => { try { localStorage.removeItem(LS_HINT); } catch (_) { /* ignore */ } };

function toCreateOptions(o) {
  const p = { ...o };
  p.challenge = b64urlToBuf(o.challenge);
  p.user = { ...o.user, id: b64urlToBuf(o.user.id) };
  if (o.excludeCredentials) p.excludeCredentials = o.excludeCredentials.map((c) => ({ ...c, id: b64urlToBuf(c.id) }));
  return p;
}
function toRequestOptions(o) {
  const p = { ...o };
  p.challenge = b64urlToBuf(o.challenge);
  if (o.allowCredentials) p.allowCredentials = o.allowCredentials.map((c) => ({ ...c, id: b64urlToBuf(c.id) }));
  return p;
}
const attToJson = (c) => ({
  id: c.id, rawId: bufToB64url(c.rawId), type: c.type,
  response: {
    clientDataJSON: bufToB64url(c.response.clientDataJSON),
    attestationObject: bufToB64url(c.response.attestationObject),
  },
});
const asrToJson = (c) => ({
  id: c.id, rawId: bufToB64url(c.rawId), type: c.type,
  response: {
    clientDataJSON: bufToB64url(c.response.clientDataJSON),
    authenticatorData: bufToB64url(c.response.authenticatorData),
    signature: bufToB64url(c.response.signature),
    userHandle: c.response.userHandle ? bufToB64url(c.response.userHandle) : null,
  },
});

// Enroll the current device for the logged-in user. Returns credential meta.
export async function enrollQuickLogin(deviceLabel) {
  const beg = (await api.post('/auth/webauthn/register/begin')).data.data;
  const cred = await navigator.credentials.create({ publicKey: toCreateOptions(beg.options) });
  const res = (await api.post('/auth/webauthn/register/complete', {
    challenge_id: beg.challenge_id, credential: attToJson(cred), device_label: deviceLabel || '',
  })).data.data;
  return res;
}

// Authenticate via the device biometric. Returns { token, user }.
export async function quickLogin(username) {
  const beg = (await api.post('/auth/webauthn/login/begin', { username: username || '' })).data.data;
  const cred = await navigator.credentials.get({ publicKey: toRequestOptions(beg.options) });
  const out = (await api.post('/auth/webauthn/login/complete', {
    challenge_id: beg.challenge_id, credential: asrToJson(cred),
  })).data.data;
  return out;
}
